/**
 * @fileoverview Bulk Upload Wizard for precedent templates.
 *
 * Purpose:
 * - Let firm admins seed an entire precedent library at once by dropping many
 *   `.docx` / `.pdf` files into a single dialog, reviewing per-file Name /
 *   Type / Category in a table, and committing them in one go.
 * - Each file is uploaded to Convex storage, then linked to a freshly created
 *   `precedentTemplates` row via `bulkCreateAndUploadTemplate`, which also
 *   schedules the existing extraction pipeline. The per-template extraction
 *   status indicators on `TemplateCard` take over once the dialog closes.
 *
 * Type detection:
 * - `.docx` is always Letter.
 * - `.pdf` defaults to Letter, then a background probe (`detectPdfFormType`)
 *   inspects AcroForm widgets and updates the row to Form when present.
 * - Users can override the suggestion before committing.
 *
 * Unsupported formats (`.doc`, `.pptx`) are added as disabled rows so the user
 * can see them, but the commit button skips them with a warning toast.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import {
  AlertCircle,
  CheckCircle2,
  FileSpreadsheet,
  FileText,
  Loader2,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from "@/ui/primitives";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import { detectPdfFormType } from "@/utils/precedents/detectPdfFormType";

const ACCEPT_ATTR = ".pdf,.docx,.dotx";
const SUPPORTED_EXTENSIONS = new Set(["pdf", "docx", "dotx"]);
const UNSUPPORTED_EXTENSIONS = new Set(["doc", "dot", "pptx"]);
const COMMIT_CONCURRENCY = 3;
const SOFT_BATCH_WARNING_THRESHOLD = 15;

type TemplateType = "letter" | "form";

type RowStatus =
  | "pending" // ready to commit
  | "detecting" // running client-side PDF probe
  | "uploading" // uploading blob to Convex storage
  | "creating" // calling bulkCreateAndUploadTemplate
  | "done"
  | "error"
  | "unsupported"; // .doc / .pptx — surfaced inline, skipped on commit

interface BulkUploadRow {
  id: string;
  file: File;
  name: string;
  templateType: TemplateType;
  categoryId: string;
  status: RowStatus;
  errorMessage?: string;
}

interface CategoryOption {
  _id: string;
  name: string;
  isSystem?: boolean;
}

interface BulkUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: Id<"organizations">;
  categories: CategoryOption[];
  /** Pre-selected category id for new rows (e.g. the section the user clicked from). */
  defaultCategoryId?: string;
}

/**
 * Strip extension, replace separators with spaces, title-case each word.
 * Preserves embedded UPPERCASE acronyms (e.g. "ASIC", "ATO") because the
 * fallback only changes the FIRST character of each word.
 */
function deriveNameFromFile(fileName: string): string {
  const base = fileName.replace(/\.[^.]+$/, "");
  const cleaned = base.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  if (!cleaned) return base;
  return cleaned
    .split(" ")
    .map((word) => {
      if (!word) return word;
      if (word === word.toUpperCase()) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

function getExtension(fileName: string): string {
  const idx = fileName.lastIndexOf(".");
  return idx === -1 ? "" : fileName.slice(idx + 1).toLowerCase();
}

function makeRowId(): string {
  return `row_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Default category for new rows. Prefers the user-supplied default, then any
 * non-system category, then the first available — guarantees a valid
 * categoryId so the row select is never empty.
 */
function pickDefaultCategoryId(
  categories: CategoryOption[],
  defaultCategoryId?: string,
): string | undefined {
  if (
    defaultCategoryId &&
    categories.some((c) => c._id === defaultCategoryId)
  ) {
    return defaultCategoryId;
  }
  const firstNonSystem = categories.find((c) => !c.isSystem);
  return firstNonSystem?._id ?? categories[0]?._id;
}

export function BulkUploadDialog({
  open,
  onOpenChange,
  orgId,
  categories,
  defaultCategoryId,
}: BulkUploadDialogProps) {
  const [rows, setRows] = useState<BulkUploadRow[]>([]);
  const [committing, setCommitting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const generateUploadUrl = useConvexMutation(
    api.settings.precedents.mutations.generateUploadUrl,
  );
  const { mutateAsync: bulkCreate } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.mutations.bulkCreateAndUploadTemplate,
    ),
  });
  const { mutateAsync: createCategory } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.categories.createCategory,
    ),
  });

  // Reset state every time the dialog closes so reopening gives a clean slate.
  useEffect(() => {
    if (!open) {
      setRows([]);
      setCommitting(false);
      setIsDragOver(false);
    }
  }, [open]);

  const fallbackCategoryId = useMemo(
    () => pickDefaultCategoryId(categories, defaultCategoryId),
    [categories, defaultCategoryId],
  );

  const handleAddFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      if (!fallbackCategoryId) {
        toast.error(
          "Create a category first, then bulk upload templates into it.",
        );
        return;
      }

      // Build new rows with status; PDFs start in "detecting" until the probe
      // resolves and updates them. .doc / .pptx are surfaced as "unsupported"
      // so users see what was skipped and can remove them.
      const additions: BulkUploadRow[] = files.map((file) => {
        const ext = getExtension(file.name);
        const name = deriveNameFromFile(file.name);
        if (UNSUPPORTED_EXTENSIONS.has(ext)) {
          return {
            id: makeRowId(),
            file,
            name,
            templateType: "letter",
            categoryId: fallbackCategoryId,
            status: "unsupported",
            errorMessage:
              ext === "doc" || ext === "dot"
                ? `Legacy .${ext} not supported — re-save as .${ext}x`
                : "PowerPoint files are not supported",
          };
        }
        if (!SUPPORTED_EXTENSIONS.has(ext)) {
          return {
            id: makeRowId(),
            file,
            name,
            templateType: "letter",
            categoryId: fallbackCategoryId,
            status: "unsupported",
            errorMessage: `Unsupported file type (.${ext || "?"})`,
          };
        }
        return {
          id: makeRowId(),
          file,
          name,
          templateType: "letter",
          categoryId: fallbackCategoryId,
          status: ext === "pdf" ? "detecting" : "pending",
        };
      });

      setRows((prev) => [...prev, ...additions]);

      // Kick off async PDF probes; updates only the matching id, so any
      // user edits to name/type/category in the meantime are preserved.
      for (const row of additions) {
        if (row.status !== "detecting") continue;
        void detectPdfFormType(row.file).then((detectedType) => {
          setRows((prev) =>
            prev.map((r) =>
              r.id === row.id && r.status === "detecting"
                ? { ...r, templateType: detectedType, status: "pending" }
                : r,
            ),
          );
        });
      }
    },
    [fallbackCategoryId],
  );

  const handleFilePicker = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleAddFiles(Array.from(e.target.files));
        e.target.value = "";
      }
    },
    [handleAddFiles],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const dropped = Array.from(e.dataTransfer.files);
      handleAddFiles(dropped);
    },
    [handleAddFiles],
  );

  const updateRow = useCallback((id: string, patch: Partial<BulkUploadRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const removeRow = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const applyTypeToAll = useCallback((nextType: TemplateType) => {
    setRows((prev) =>
      prev.map((r) =>
        r.status === "unsupported" ||
        r.status === "done" ||
        r.status === "creating" ||
        r.status === "uploading"
          ? r
          : { ...r, templateType: nextType },
      ),
    );
  }, []);

  const applyCategoryToAll = useCallback((nextCategoryId: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.status === "done" ||
        r.status === "creating" ||
        r.status === "uploading"
          ? r
          : { ...r, categoryId: nextCategoryId },
      ),
    );
  }, []);

  const handleCreateCategoryInline = useCallback(async () => {
    const name = window.prompt("New category name");
    if (!name) return;
    try {
      const newId = await createCategory({ orgId, name: name.trim() });
      toast.success(`Category "${name.trim()}" created`);
      // Apply to every row that's still using the fallback category, since
      // the user typically created the category to drop these files into.
      setRows((prev) =>
        prev.map((r) =>
          r.categoryId === fallbackCategoryId ? { ...r, categoryId: newId } : r,
        ),
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to create category"));
    }
  }, [createCategory, orgId, fallbackCategoryId]);

  // Commit: upload each file to Convex storage, then call the bulk mutation
  // to insert the template row + schedule extraction. Runs with a small
  // concurrency cap so we don't stall the browser network queue.
  const handleCommit = useCallback(async () => {
    const committable = rows.filter(
      (r) => r.status === "pending" || r.status === "error",
    );
    if (committable.length === 0) {
      toast.info("Nothing to upload");
      return;
    }

    // Validate each row has a name + category before doing any work, so we
    // bail loudly instead of silently skipping rows mid-loop.
    for (const row of committable) {
      if (!row.name.trim()) {
        toast.error(`"${row.file.name}" needs a template name`);
        return;
      }
      if (!row.categoryId) {
        toast.error(`"${row.file.name}" needs a category`);
        return;
      }
    }

    setCommitting(true);

    let succeeded = 0;
    let failed = 0;

    const queue = [...committable];

    const processOne = async (row: BulkUploadRow): Promise<void> => {
      try {
        updateRow(row.id, { status: "uploading", errorMessage: undefined });
        const uploadUrl = await generateUploadUrl({});
        const uploadResult = await fetch(uploadUrl, {
          method: "POST",
          headers: {
            "Content-Type": row.file.type || "application/octet-stream",
          },
          body: row.file,
        });
        if (!uploadResult.ok) {
          throw new Error(`Upload failed (${uploadResult.status})`);
        }
        const { storageId } = (await uploadResult.json()) as {
          storageId: Id<"_storage">;
        };

        updateRow(row.id, { status: "creating" });
        await bulkCreate({
          orgId,
          name: row.name.trim(),
          templateType: row.templateType,
          categoryId: row.categoryId as Id<"templateCategories">,
          storageId,
          fileName: row.file.name,
        });

        updateRow(row.id, { status: "done", errorMessage: undefined });
        succeeded += 1;
      } catch (err) {
        const message = extractErrorMessage(err, "Upload failed");
        updateRow(row.id, { status: "error", errorMessage: message });
        failed += 1;
      }
    };

    // Simple concurrency-limited worker pool — pulls from the shared queue.
    const workers: Array<Promise<void>> = [];
    for (let i = 0; i < Math.min(COMMIT_CONCURRENCY, queue.length); i++) {
      workers.push(
        (async () => {
          while (queue.length > 0) {
            const row = queue.shift();
            if (!row) break;
            await processOne(row);
          }
        })(),
      );
    }
    await Promise.all(workers);

    setCommitting(false);

    if (failed === 0) {
      toast.success(
        `Uploaded ${succeeded} template${succeeded === 1 ? "" : "s"} — extraction running in the background.`,
      );
      onOpenChange(false);
    } else {
      toast.warning(
        `${succeeded} uploaded, ${failed} failed. Review the errors and retry the failed rows.`,
      );
    }
  }, [rows, generateUploadUrl, bulkCreate, orgId, updateRow, onOpenChange]);

  const committableCount = rows.filter(
    (r) => r.status === "pending" || r.status === "error",
  ).length;
  const detectingCount = rows.filter((r) => r.status === "detecting").length;
  const isAnyInFlight = rows.some(
    (r) => r.status === "uploading" || r.status === "creating",
  );

  return (
    <Dialog open={open} onOpenChange={committing ? undefined : onOpenChange}>
      <DialogContent
        className="sm:max-w-4xl"
        onEscapeKeyDown={(e) => {
          if (committing) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (committing) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Add Templates</DialogTitle>
          <DialogDescription>
            Select or drag and drop templates. We detect whether a template is a
            letter or form and suggest a category based on the file name. You
            can adjust suggestions before adding templates.
          </DialogDescription>
        </DialogHeader>

        {/* Drop zone — also serves as "Add more files" once rows exist */}
        <div
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragOver(false);
          }}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors",
            isDragOver
              ? "border-primary bg-primary/5"
              : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/40",
          )}
        >
          <Upload className="size-7 text-primary/40" />
          <div className="text-center">
            <p className="text-sm font-medium">
              {rows.length === 0
                ? "Drag and drop files here, or click to browse"
                : "Drop more files to add to the batch"}
            </p>
            <p className="text-xs text-primary/50">
              .pdf, .docx, and .dotx only
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT_ATTR}
            className="hidden"
            onChange={handleFilePicker}
          />
        </div>

        {rows.length > 0 && (
          <>
            {/* Bulk header controls */}
            <div className="flex flex-wrap items-center gap-2 border-b pb-3">
              <span className="text-xs font-medium text-primary/60">
                Apply to all:
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={() => applyTypeToAll("letter")}
                disabled={committing}
              >
                <FileText className="size-3" />
                Letter
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={() => applyTypeToAll("form")}
                disabled={committing}
              >
                <FileSpreadsheet className="size-3" />
                Form
              </Button>
              <div className="ml-2 flex items-center gap-2">
                <Select
                  onValueChange={(v) => {
                    if (v === "__create__") {
                      void handleCreateCategoryInline();
                      return;
                    }
                    applyCategoryToAll(v);
                  }}
                  disabled={committing}
                >
                  <SelectTrigger className="h-7 w-[200px] text-xs">
                    <SelectValue placeholder="Set category for all…" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem
                        key={cat._id}
                        value={cat._id}
                        className="text-xs"
                      >
                        {cat.name}
                        {cat.isSystem && " (System)"}
                      </SelectItem>
                    ))}
                    <SelectItem value="__create__" className="text-xs">
                      <span className="flex items-center gap-1">
                        <Plus className="size-3" /> Create new category…
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="ml-auto text-xs text-primary/50">
                {rows.length} file{rows.length === 1 ? "" : "s"}
                {detectingCount > 0 && <> · {detectingCount} detecting…</>}
              </div>
            </div>

            {/* Soft warning for large batches */}
            {committableCount > SOFT_BATCH_WARNING_THRESHOLD && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
                <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
                <span>
                  This will trigger {committableCount} AI extraction calls.
                  Extraction continues in the background after the dialog closes
                  — you can leave this page once uploads complete.
                </span>
              </div>
            )}

            {/* Review table */}
            <div className="max-h-[50vh] overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-secondary/40 text-xs text-primary/60">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">File</th>
                    <th className="px-3 py-2 text-left font-medium">Name</th>
                    <th className="px-3 py-2 text-left font-medium">Type</th>
                    <th className="px-3 py-2 text-left font-medium">
                      Category
                    </th>
                    <th className="w-10 px-2" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <BulkUploadTableRow
                      key={row.id}
                      row={row}
                      categories={categories}
                      committing={committing}
                      onChangeName={(name) => updateRow(row.id, { name })}
                      onChangeType={(templateType) =>
                        updateRow(row.id, { templateType })
                      }
                      onChangeCategory={(categoryId) =>
                        updateRow(row.id, { categoryId })
                      }
                      onRemove={() => removeRow(row.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={committing}
          >
            {rows.some((r) => r.status === "done") && !committing
              ? "Close"
              : "Cancel"}
          </Button>
          <Button
            type="button"
            onClick={handleCommit}
            disabled={
              committing ||
              isAnyInFlight ||
              committableCount === 0 ||
              detectingCount > 0
            }
            className="gap-1.5"
          >
            {committing && <Loader2 className="size-3.5 animate-spin" />}
            {committing
              ? "Uploading…"
              : `Upload ${committableCount} template${committableCount === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Per-row component
// ---------------------------------------------------------------------------

interface BulkUploadTableRowProps {
  row: BulkUploadRow;
  categories: CategoryOption[];
  committing: boolean;
  onChangeName: (name: string) => void;
  onChangeType: (type: TemplateType) => void;
  onChangeCategory: (categoryId: string) => void;
  onRemove: () => void;
}

function BulkUploadTableRow({
  row,
  categories,
  committing,
  onChangeName,
  onChangeType,
  onChangeCategory,
  onRemove,
}: BulkUploadTableRowProps) {
  const isUnsupported = row.status === "unsupported";
  const isDone = row.status === "done";
  const isLocked =
    isDone ||
    row.status === "uploading" ||
    row.status === "creating" ||
    isUnsupported;

  return (
    <tr
      className={cn(
        "border-t",
        isUnsupported && "bg-red-50/40 dark:bg-red-950/10",
        isDone && "bg-emerald-50/40 dark:bg-emerald-950/10",
      )}
    >
      <td className="px-3 py-2 align-top">
        <div className="flex max-w-[180px] items-start gap-2">
          {row.templateType === "form" ? (
            <FileSpreadsheet className="mt-0.5 size-4 shrink-0 text-primary/40" />
          ) : (
            <FileText className="mt-0.5 size-4 shrink-0 text-primary/40" />
          )}
          <div className="min-w-0">
            <p
              className="truncate text-xs font-medium text-primary/80"
              title={row.file.name}
            >
              {row.file.name}
            </p>
            <p className="text-[10px] text-primary/40">
              {(row.file.size / 1024).toFixed(0)} KB
            </p>
            <RowStatusPill row={row} />
          </div>
        </div>
      </td>
      <td className="px-3 py-2 align-top">
        <Input
          value={row.name}
          onChange={(e) => onChangeName(e.target.value)}
          disabled={isLocked || committing}
          className="h-7 text-xs"
          placeholder="Template name"
        />
      </td>
      <td className="px-3 py-2 align-top">
        <Select
          value={row.templateType}
          onValueChange={(v) => onChangeType(v as TemplateType)}
          disabled={isLocked || committing || row.status === "detecting"}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="letter" className="text-xs">
              Letter
            </SelectItem>
            <SelectItem value="form" className="text-xs">
              Form
            </SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 py-2 align-top">
        <Select
          value={row.categoryId}
          onValueChange={onChangeCategory}
          disabled={isLocked || committing}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat._id} value={cat._id} className="text-xs">
                {cat.name}
                {cat.isSystem && " (System)"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-2 py-2 align-top">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="size-7 p-0 text-primary/40 hover:text-red-500"
          onClick={onRemove}
          disabled={
            committing &&
            (row.status === "uploading" || row.status === "creating")
          }
          title="Remove"
        >
          <Trash2 className="size-3.5" />
        </Button>
      </td>
    </tr>
  );
}

function RowStatusPill({ row }: { row: BulkUploadRow }) {
  switch (row.status) {
    case "detecting":
      return (
        <span className="mt-1 inline-flex items-center gap-1 text-[10px] text-primary/50">
          <Spinner className="size-2.5" /> detecting type…
        </span>
      );
    case "uploading":
      return (
        <span className="mt-1 inline-flex items-center gap-1 text-[10px] text-primary/60">
          <Spinner className="size-2.5" /> uploading…
        </span>
      );
    case "creating":
      return (
        <span className="mt-1 inline-flex items-center gap-1 text-[10px] text-primary/60">
          <Spinner className="size-2.5" /> creating…
        </span>
      );
    case "done":
      return (
        <span className="mt-1 inline-flex items-center gap-1 text-[10px] text-emerald-700">
          <CheckCircle2 className="size-2.5" /> uploaded
        </span>
      );
    case "error":
      return (
        <span
          className="mt-1 inline-flex items-start gap-1 text-[10px] text-red-600"
          title={row.errorMessage}
        >
          <AlertCircle className="mt-px size-2.5 shrink-0" />
          <span className="break-words">{row.errorMessage ?? "Failed"}</span>
        </span>
      );
    case "unsupported":
      return (
        <Badge
          variant="outline"
          className="mt-1 border-red-200 px-1.5 py-0 text-[9px] text-red-600"
        >
          {row.errorMessage ?? "Unsupported"}
        </Badge>
      );
    default:
      return null;
  }
}
